import { useEffect, useState } from 'react';

import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { getLocationSuggestions, getServices, getUnifiedSearch } from '../../services/apiService';
import { LocationSuggestion, LocationSuggestionKind, Service } from '../../types';
import { getListingShuffleSeed } from '../../utils/listingShuffleSeed';
import { SearchTab } from '../../utils/tripSearchResults';

export interface ListingSuggestion {
  kind: 'accommodation' | 'service';
  id: number;
  name: string;
  location: string;
}

export interface WhereSuggestions {
  destinations: LocationSuggestion[];
  /** The inventory the destinations and their counts are drawn from. */
  destinationKind: LocationSuggestionKind;
  listings: ListingSuggestion[];
}

const SUGGESTION_DEBOUNCE_MS = 250;
const LISTING_MIN_CHARACTERS = 2;
const LISTING_LIMIT = 5;
const SUGGESTED_SERVICE_LIMIT = 3;

const LISTING_NOUNS: Record<LocationSuggestionKind, { one: string; many: string }> = {
  accommodation: { one: 'stay', many: 'stays' },
  service: { one: 'service', many: 'services' },
};

/** "1 stay", "12 services": the count line under a suggested destination. */
export function listingCountLabel(count: number, kind: LocationSuggestionKind): string {
  const nouns = LISTING_NOUNS[kind];
  return `${count} ${count === 1 ? nouns.one : nouns.many}`;
}

const destinationKindOf = (tab: SearchTab): LocationSuggestionKind =>
  tab === 'services' ? 'service' : 'accommodation';

const toServiceSuggestions = (services: Service[]): ListingSuggestion[] =>
  services.map(({ id, name, location }) => ({ kind: 'service', id, name, location }));

async function fetchListings(trimmedQuery: string, tab: SearchTab): Promise<ListingSuggestion[]> {
  // Before anything is typed, the services tab offers a few services as examples.
  if (tab === 'services' && trimmedQuery.length === 0) {
    return toServiceSuggestions(
      await getServices({
        shuffle_seed: getListingShuffleSeed(),
        limit: SUGGESTED_SERVICE_LIMIT,
      }),
    );
  }
  if (trimmedQuery.length < LISTING_MIN_CHARACTERS) return [];
  if (tab === 'services') {
    return toServiceSuggestions(await getServices({ search: trimmedQuery, limit: LISTING_LIMIT }));
  }
  const unifiedResults = await getUnifiedSearch({ search: trimmedQuery, limit: LISTING_LIMIT });
  return unifiedResults.map(({ type, data }) => ({
    kind: type,
    id: data.id,
    name: data.name,
    location: data.location,
  }));
}

async function fetchSuggestions(
  trimmedQuery: string,
  destinationLimit: number | undefined,
  tab: SearchTab,
): Promise<WhereSuggestions> {
  const destinationKind = destinationKindOf(tab);
  const [destinations, listings] = await Promise.all([
    getLocationSuggestions(trimmedQuery || undefined, destinationLimit, destinationKind),
    fetchListings(trimmedQuery, tab),
  ]);
  return { destinations, destinationKind, listings };
}

/**
 * Destination and listing suggestions for the Where field, debounced and stale-safe.
 * The services tab suggests places from service inventory, matches services only,
 * and offers a few example services before anything is typed.
 */
export function useWhereSuggestions(
  query: string,
  options?: { destinationLimit?: number; tab?: SearchTab },
): WhereSuggestions {
  const destinationLimit = options?.destinationLimit;
  const tab = options?.tab ?? 'discover';
  const debouncedQuery = useDebouncedValue(query.trim(), SUGGESTION_DEBOUNCE_MS);
  const [suggestions, setSuggestions] = useState<WhereSuggestions>({
    destinations: [],
    destinationKind: destinationKindOf(tab),
    listings: [],
  });

  useEffect(() => {
    let isCurrent = true;
    fetchSuggestions(debouncedQuery, destinationLimit, tab).then((result) => {
      if (isCurrent) setSuggestions(result);
    });
    return () => {
      isCurrent = false;
    };
  }, [debouncedQuery, destinationLimit, tab]);

  return suggestions;
}
